import { Address } from "shared/utils/fieldsHelper/companyFields";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import CultureLabel from "components/Culture/CultureLabel";
import { DeletedRow } from "components/DetailPage/DeletedRow";
import NotValidatedRow from "components/DetailPage/NotValidatedRow";
import PhoneRow from "components/DetailPage/PhoneRow";
import CulturesTemplateDropdown from "components/dropdowns/reactselect/CulturesTemplateDropdown";
import StatusDropdown from "components/dropdowns/reactselect/StatusDropdown";
import { SidePanelDetailSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelDetailSectionTemplate";
import { convertToPhoneNumbers, PhoneInput } from "components/PhoneInput";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import Status from "components/Status/Status";
import { useHasAnyTags } from "components/Tag/hooks";
import TagList from "components/Tag/TagList";
import TagsSelect from "components/Tag/TagsSelect";
import TwitterLink from "components/TwitterLink";
import UrlInput from "components/urlinput";
import { AddressInnerForm } from "components/ValidatedFields/AddressInnerForm";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import WebsiteLink from "components/Website/WebsiteLink";
import ContactEmailRow from "contact/detail/components/ContactEmailRow";
import { ContactName } from "contact/detail/components/ContactName";
import { EmailWithBlacklist } from "contact/detail/components/EmailWithBlacklist";
import { GenderRadio } from "contact/detail/components/GenderRadio";
import { contactToFormState, getInstruction, ContactFormState } from "shared/utils/fieldsHelper/contactFields";
import React from "react";
import { isNullOrEmptyString } from "shared/utils";
import { getGenderName } from "utilities/genderUtils";
import { ContactWorksAtSection } from "./ContactWorksAtSection";
import { ContactAliasesSection } from "./ContactAliasesSection";
import { invalidateContactCache } from "utilities/api/contactQuery";
import { useQueryClient } from "@tanstack/react-query";
import FormattedAddress from "components/Atoms/FormattedAddress";
import { useHasMultipleStatuses } from "components/Status/hooks";

interface Props {
  sidePanelData: server.dto.ContactSidePanelData
  refetch: () => Promise<void>
}

export const ContactSidePanelDetails = (props: Props) => {
  const sidePanelData = props.sidePanelData;
  const { contact } = sidePanelData;
  const [formState, setFormState] = React.useState<ContactFormState>(contactToFormState(sidePanelData.contact, serverReference));
  const [tags, setTags] = React.useState<server.dto.TagSmall[]>(sidePanelData.contact.tags);

  React.useEffect(() => {
    if (props.sidePanelData) {
      setFormState(contactToFormState(props.sidePanelData.contact, serverReference));
      setTags(props.sidePanelData.contact.tags);
    }
  }, [props.sidePanelData]);

  React.useEffect(() => {
    if (props.sidePanelData) {
      setFormState(contactToFormState(props.sidePanelData.contact, serverReference));
      setTags(props.sidePanelData.contact.tags);
    }
  }, [props.sidePanelData]);

  const hasMultipleStatuses = useHasMultipleStatuses("contact", contact.clientId);
  const hasAnyTags = useHasAnyTags("contact");
  const canInPageEdit = sidePanelData.contact.permissions.canModify;

  const onValueChanged = (key: string, value: any) => {
    if (key === "name") {
      const [firstName, insertion, lastName] = value as string[];
      setFormState(prevState => ({
        ...prevState,
        firstName,
        insertion,
        lastName
      }));
    }
    else if (key === "position") {
      setFormState(prevState => ({
        ...prevState,
        position: value
      }));
    } else if (key === "email") {
      setFormState(prevState => ({
        ...prevState,
        emailAddresses: value[0],
        isEmailBlacklisted: value[1]
      }));
    } else if (key === "homeAddress") {
      setFormState(prevState => ({
        ...prevState,
        homeAddress: value
      }));
    } else if (key === "language") {
      setFormState(prevState => ({
        ...prevState,
        culture: value
      }));
    }
    else if (key === "mobileNumbers") {
      setFormState(prevState => ({
        ...prevState,
        mobileNumbers: value
      }));
    }
    else if (key === "landline") {
      setFormState(prevState => ({
        ...prevState,
        landlineNumbers: value
      }));
    } else if (key === "status") {
      setFormState(prevState => ({
        ...prevState,
        status: value
      }));
    } else if (key === "owner") {
      setFormState(prevState => ({
        ...prevState,
        owner: value
      }));
    } else if (key === "tags") {
      setTags(value);
    } else if (key === "gender") {
      setFormState(prevState => ({
        ...prevState,
        gender: value
      }));
    } else if (key === "department") {
      setFormState(prevState => ({
        ...prevState,
        department: value
      }));
    } else if (key === "salutation") {
      setFormState(prevState => ({
        ...prevState,
        salutation: value
      }));
    } else if (key === "attn") {
      setFormState(prevState => ({
        ...prevState,
        attn: value
      }));
    } else if (key === "twitter") {
      setFormState(prevState => ({
        ...prevState,
        twitter: value
      }));
    }
    else if (key === "website") {
      setFormState(prevState => ({
        ...prevState,
        website: value
      }));
    } else if (key === "facebook") {
      setFormState(prevState => ({
        ...prevState,
        facebook: value
      }));
    } else if (key === "socialSecurity") {
      setFormState(prevState => ({
        ...prevState,
        socialSecurity: value
      }));
    }
    else {
      console.warn("Unhandled value change in ContactSidePanelDetails", key, value);
    }
  }

  const queryClient = useQueryClient();

  const onSuccessfulUpdate = async () => {
    await props.refetch();
    invalidateContactCache(queryClient, contact.contactId);
  }

  return <SidePanelDetailSectionTemplate
    isInPageEditable={canInPageEdit}
    onSuccessfulUpdate={onSuccessfulUpdate}
    onValueChanged={onValueChanged}
    fields={[{
      key: "isDeleted",
      label: "",
      hideLabel: true,
      hidden: !sidePanelData.contact.isDeleted,
      render: () => <DeletedRow />
    }, {
      key: "isValidated",
      label: "",
      hideLabel: true,
      hidden: sidePanelData.contact.isValidated,
      render: () => <NotValidatedRow
        canApprove={sidePanelData.contact.permissions.canModify}
        sublabel={RESX.Planning.InputFromWebform}
        approveEndpoint={() => window.CoreApi.Contact.Validate({
          contactId: sidePanelData.contact.contactId
        })} />
    }, {
      key: "name",
      label: RESX.GeneralLabels.Name,
      render: () => <div>{[formState.firstName, formState.insertion, formState.lastName].filter(Boolean).join(" ")}</div>,
      inPageEdit: {
        initialValue: [formState.firstName, formState.insertion, formState.lastName],
        render: ({ value, onChange, submit, cancel }) =>
          <ContactName
            focusOnMount
            firstName={value[0]}
            insertion={value[1]}
            lastName={value[2]}
            onChange={(firstName, insertion, lastName) => onChange([firstName, insertion, lastName])}
            onEnter={submit}
            onEsc={cancel}
            width={130}
          />,
        submitChange: ([firstName, insertion, lastName]: string[]) => {
          const instruction = getInstruction(contact, formState, {
            name: {
              firstName: firstName,
              insertion: insertion,
              lastName: lastName,
            }
          });
          return window.CoreApi.Contact.UpdateDetails(instruction)
        },
        type: "confirmOrCancelButton"
      }
    }, {
      key: "email",
      label: RESX.GeneralLabels.Email,
      isEmpty: isNullOrEmptyString(formState.emailAddresses),
      render: () => <ContactEmailRow
        emailAddresses={formState.emailAddresses}
        emailBlacklisted={formState.isEmailBlacklisted}
      />,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }) => <EmailWithBlacklist
          value={value}
          onChange={v => onChange(v)}
          onEnter={submit}
          onEsc={cancel}
        />,
        fullWidth: true,
        submitChange: (value: [string, boolean]) => {
          const instruction = getInstruction(contact, formState, { emailAddresses: value[0], emailBlacklisted: value[1] });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        },
        type: "confirmOrCancelButton",
        initialValue: [formState.emailAddresses, formState.isEmailBlacklisted],
      }
    }, {
      key: "mobileNumbers",
      label: RESX.Contact.Mobile,
      render: () => <PhoneRow numbers={convertToPhoneNumbers(formState.mobileNumbers)} />,
      isEmpty: isNullOrEmptyString(formState.mobileNumbers),
      inPageEdit: {
        initialValue: formState.mobileNumbers,
        render: ({ value, onChange, submit, cancel }) => <PhoneInput
          focusOnMount
          value={value}
          onChange={onChange}
          onEnter={submit}
          onEsc={cancel}
        />,
        fullWidth: true,
        type: "confirmOrCancelButton",
        submitChange: (value: string) => {
          const instruction = getInstruction(contact, formState, { mobileNumbers: convertToPhoneNumbers(value) });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }
    }, {
      key: "landline",
      label: RESX.ContactDetails.PhoneNumber,
      render: () => <PhoneRow numbers={convertToPhoneNumbers(formState.landlineNumbers)} />,
      isEmpty: isNullOrEmptyString(formState.landlineNumbers),
      inPageEdit: {
        initialValue: formState.landlineNumbers,
        fullWidth: true,
        render: ({ value, onChange, submit, cancel }) => <PhoneInput
          focusOnMount
          value={value}
          onChange={onChange}
          onEnter={submit}
          onEsc={cancel}
        />,
        type: "confirmOrCancelButton",
        submitChange: (value: string) => {
          const instruction = getInstruction(contact, formState, { landlineNumbers: convertToPhoneNumbers(value) });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }
    }, {
      key: "language",
      label: RESX.Language.resxLanguage,
      render: () => <CultureLabel culture={serverReference.cultures.find(c => c.value === formState.culture)} />,
      inPageEdit: {
        initialValue: formState.culture,
        fullWidth: true,
        render: ({ value, onChange }) => (
          <CulturesTemplateDropdown
            value={value}
            onChange={c => onChange(c, true)}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value: server.dto.LCID) => {
          const instruction = getInstruction(contact, formState, { culture: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }
    }, {
      key: "gender",
      label: RESX.Contact.Gender,
      render: () => getGenderName(formState.gender),
      inPageEdit: {
        initialValue: formState.gender,
        render: ({ value, onChange }) => (
          <GenderRadio
            value={value}
            onChange={v => onChange(v, true)}
          />
        ),
        type: "custom",
        submitChange: (value: server.dto.Genders) => {
          const instruction = getInstruction(contact, formState, { gender: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }
    }, {
      key: "position",
      label: RESX.Contact.Position,
      render: () => formState.position,
      isEmpty: isNullOrEmptyString(formState.position),
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }) =>
          <OptionalTextInput
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.contact.maxPositionLength}
            focusOnMount
          />,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(contact, formState, { position: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        },
        type: "confirmOrCancelButton",
        initialValue: formState.position,
      }
    }, {
      key: "department",
      label: RESX.Contact.Department,
      render: () => formState.department,
      isEmpty: isNullOrEmptyString(formState.department),
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }) =>
          <OptionalTextInput
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.contact.maxDepartmentLength}
            focusOnMount
          />,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(contact, formState, { department: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        },
        type: "confirmOrCancelButton",
        initialValue: formState.department,
      }
    }, {
      key: "tags",
      label: RESX.Tag.resxTag,
      render: () => <TagList tags={tags} version="short" />,
      isEmpty: tags.length === 0,
      hidden: !hasAnyTags,
      inPageEdit: {
        render: ({ value, onChange }) => (
          <TagsSelect
            mainArea="contact"
            value={value}
            onChange={onChange}
            inPageEditing="edit"
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: tags,
        submitChange: (tags: server.dto.TagSmall[]) => {
          return window.CoreApi.Contact.UpdateTags({ itemId: contact.contactId, tagsIds: tags.map(tag => tag.tagId) });
        }
      }
    }, {
      key: "salutation",
      label: RESX.Contact.Salutation,
      render: () => formState.salutation,
      isEmpty: isNullOrEmptyString(formState.salutation),
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }) =>
          <OptionalTextInput
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.contact.maxSalutationLength}
            focusOnMount
          />,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(contact, formState, { salutation: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        },
        type: "confirmOrCancelButton",
        initialValue: formState.salutation,
      }
    }, {
      key: "attn",
      label: RESX.Contact.AttnLineOnEnvelope,
      render: () => formState.attn,
      isEmpty: isNullOrEmptyString(formState.attn),
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }) =>
          <OptionalTextInput
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.contact.maxAttnLength}
            focusOnMount
          />,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(contact, formState, { attn: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        },
        type: "confirmOrCancelButton",
        initialValue: formState.attn,
      }
    }, {
      key: "homeAddress",
      label: RESX.Contact.HomeAddress,
      render: () => <FormattedAddress address={contact.address} />,
      isEmpty: !contact.address.formatted?.length,
      inPageEdit: {
        initialValue: formState.homeAddress,
        useCustomSavingState: true,
        fullWidth: true,
        render: ({ value, onChange, submit, isSaving }) => (
          <div style={{ paddingLeft: 0 }} className="main-section-template-fields-padding">
            <AddressInnerForm
              address={value}
              onChange={onChange}
              submit={submit}
              isSaving={isSaving}
            />
          </div>
        ),
        type: "confirmOrCancelButton",
        submitChange: (value: Address) => {
          const instruction = getInstruction(contact, formState, {
            address: {
              addressLine1: value.addressLine1,
              addressLine2: value.addressLine2,
              postalCode: value.postalCode,
              city: value.city,
              stateId: value.state.key
            }
          });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }
    }, {
      key: "socialSecurity",
      label: RESX.Contact.SocialSecurity,
      render: () => <div>{formState.socialSecurity}</div>,
      isEmpty: isNullOrEmptyString(formState.socialSecurity),
      inPageEdit: {
        fullWidth: true,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.contact.maxSocialSecurityLength}
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: formState.socialSecurity,
        submitChange: (value: string) => {
          const instruction = getInstruction(contact, formState, {
            socialSecurity: value,
          });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }
    }, {
      key: "twitter",
      label: RESX.Contact.Twitter,
      render: () => <TwitterLink twitter={formState.twitter} />,
      isEmpty: isNullOrEmptyString(formState.twitter),
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }) =>
          <OptionalTextInput
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.contact.maxTwitterLength}
            focusOnMount
          />,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(contact, formState, { twitter: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        },
        type: "confirmOrCancelButton",
        initialValue: formState.twitter,
      }
    }, {
      key: "website",
      label: RESX.GeneralLabels.Website,
      render: () => <WebsiteLink website={formState.website} />,
      isEmpty: isNullOrEmptyString(formState.website),
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }) =>
          <UrlInput
            focusOnMount
            value={value}
            onValueChange={onChange}
            validatorKey="contactWebsite"
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.contact.maxNetworkPathLength}
          />,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(contact, formState, { website: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        },
        type: "confirmOrCancelButton",
        initialValue: formState.website,
      }
    }, {
      key: "facebook",
      label: "Facebook",
      render: () => <WebsiteLink website={formState.facebook} />,
      isEmpty: isNullOrEmptyString(formState.facebook),
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }) =>
          <UrlInput
            focusOnMount
            value={value}
            onValueChange={onChange}
            validatorKey="contactFacebook"
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.contact.maxNetworkPathLength}
          />,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(contact, formState, { facebook: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        },
        type: "confirmOrCancelButton",
        initialValue: formState.facebook,
      }
    }, {
      key: "status",
      label: RESX.Status.resxStatus,
      render: () => <Status status={formState.status} />,
      hidden: !hasMultipleStatuses,
      inPageEdit: {
        render: ({ value, onChange }) => (
          <StatusDropdown
            value={value}
            onChange={onChange}
            mainArea="contact"
            clientId={sidePanelData.contact.clientId}
            options={undefined}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: formState.status,
        fullWidth: true,
        submitChange: (value: server.dto.StatusSmall) => {
          const instruction = getInstruction(contact, formState, { statusId: value.statusId });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }
    }, {
      key: "owner",
      label: RESX.GeneralLabels.Owner,
      render: () => <UserWithAvatar user={formState.owner} avatarSize="avatar-30" />,
      isEmpty: !formState.owner,
      inPageEdit: {
        hoverEdit: true,
        render: ({ onChange, cancel, value }) => (
          <div className="main-section-template-fields-padding">
            <UserModalPicker
              inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
              canCreateNew={false}
            />
          </div>
        ),
        type: "custom",
        initialValue: formState.owner,
        submitChange: (value: server.dto.UserSmall) => {
          const instruction = getInstruction(contact, formState, {
            ownerUserId: value.userId,
          });

          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }
    }]}
    additionalContent={<>
      <ContactWorksAtSection worksAt={sidePanelData.worksAt} />
      <ContactAliasesSection
        aliases={sidePanelData.aliases}
        canModify={canInPageEdit}
        onDeleted={props.refetch}
      />
    </>}
  />
}
